<template>
  <b-modal
    id="modal-resolved"
    ref="modal-resolved"
    centered
    title="Alert Checked"
    size="lg"
    modal-class="mc-control-tower__resolve"
    @show="handleModalShow"
  >
    <div class="__table-top">
      <span>
        {{ `${momentDateShortMonth(period[0])} - ${momentDateShortMonth(period[1])}` }}
      </span>
      <b-input-group size="sm" class="mc-has-search mc-input" @click="focusSearch">
        <template #prepend>
          <i class="mc-icon icon-fill_search"></i>
        </template>
        <b-form-input
          ref="searchInput"
          :value="search"
          :disabled="isBusy"
          :placeholder="$t('global.search') + '...'"
          @input="handleSearchChange"
        />
        <template v-if="search" #append>
          <button class="clear-search-btn" :disabled="isBusy" @click="handleClearSearch">
            <i class="pe-2 mc-icon icon-outline_close"></i>
          </button>
        </template>
      </b-input-group>
    </div>
    <div class="__table-container">
      <b-table
        ref="table-ref"
        :fields="fields"
        :items="items"
        :busy="isBusy"
        class="-w-auto text-nowrap sticky-head-table border"
        show-empty
      >
        <template #empty>
          <div v-show="!isBusy" class="text-center my-4">
            {{ $t('global.emptyTable') }}
          </div>
        </template>
        <template #cell(alert)="data">
          <span v-if="hasRuleNameScope">
            {{
              hasRuleNameScope && data.item.alertRule?.label
                ? data.item.alertRule.label
                : $i18n.locale === 'id'
                ? data.value.descriptionIdn
                : data.value.descriptionEng
            }}
            <p v-if="data.item.alertRule" class="text-muted">
              {{ $i18n.locale === 'id' ? data.value.descriptionIdn : data.value.descriptionEng }}
            </p>
          </span>
          <span v-else>
            {{ $i18n.locale === 'id' ? data.value.descriptionIdn : data.value.descriptionEng }}
          </span>
        </template>
        <template #cell(status)="data">
          <span class="mc-status-cell d-flex justify-content-center">
            <i
              v-b-tooltip.hover="
                data.item.isFalseAlert
                  ? $t('notification.text.wrongNotification')
                  : $t('controlTower.detail.checked')
              "
              class="font-size-20"
              :class="
                data.item.isFalseAlert
                  ? 'icon-danger icon-outline_bell-off'
                  : 'icon-success icon-outline_checkmark-circle'
              "
            ></i>
          </span>
        </template>
        <template #cell(resolvedOn)="data">
          <div class="d-flex flex-column">
            <span>{{ momentDateShortMonth(data.item.resolvedOn) }}</span>
            <span class="text-muted">
              {{ momentTimeSeconds(data.item.resolvedOn) }} {{ getLabelTimezone() }}
            </span>
          </div>
        </template>
        <template #cell(vehicle)="data">
          <span>{{ data.value.licensePlate }}</span>
        </template>
        <template #cell(action)="data">
          <McButton
            :text="$t('global.action.seeDetail')"
            @click="openDetailNotification(data.item)"
          />
        </template>
      </b-table>
      <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>
      <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
        <b-skeleton width="2.5%" />
        <b-skeleton width="97.5%" />
      </div>
    </div>
    <template #modal-footer="{ cancel }">
      <McButton
        variant="secondary"
        class="me-2"
        :text="$t('global.action.close')"
        @click="cancel()"
      />
    </template>
  </b-modal>
</template>
<script>
import { GET_RAW_ALERTS_WITH_ADDRESS } from '@/services/api/driverscore/score'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
  },
  mixins: [Helper, MixPanel],
  props: {
    period: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      items: [],
      page: 1,
      limit: 10,
      totalCount: 0,
      search: '',
      isPagination: true,
      isBusy: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    fields() {
      return [
        {
          key: 'alert',
          label: this.$t('global.object.notification'),
        },
        {
          key: 'status',
          label: 'Status',
        },
        {
          key: 'resolvedOn',
          label: 'Resolved Date',
        },
        {
          key: 'vehicle',
          label: this.$t('global.object.licensePlate'),
        },
        {
          key: 'action',
          label: this.$t('global.field.action'),
        },
      ]
    },
    hasNextPage() {
      if (!this.isPagination) return false

      if (!this.totalCount || this.totalCount === 0) return false
      return this.page * this.limit < this.totalCount
    },
    hasRuleNameScope() {
      return this.hasAccess('e4J-alert-rule-name-as-title:read')
    },
  },
  methods: {
    handleModalShow() {
      this.getData(
        {
          pagination: {
            page: this.page,
            show: this.limit,
          },
        },
        true
      )
    },
    async getData(opts, reset = false) {
      this.isBusy = true
      const res = await this.$apollo.query({
        query: GET_RAW_ALERTS_WITH_ADDRESS,
        variables: {
          filter: {
            isUrgent: this.hasAccess(['e4e-alert-center-urgent:read']) ? true : undefined,
            isImportant: true,
            isResolved: true,
            orderBy: JSON.stringify({ createdOn: 'desc' }),
            startDate: moment(this.period[0]).set('second', 0).toISOString(),
            endDate: moment(this.period[1]).set('second', 59).toISOString(),
            ...opts,
          },
        },
        fetchPolicy: 'no-cache',
      })
      if (!res || res.errors) return
      this.items = reset
        ? res.data.rawAlerts.rawAlerts.data
        : [...this.items, ...res.data.rawAlerts.rawAlerts.data]
      this.totalCount = res.data.rawAlerts.rawAlerts.pagination.total
      this.page = res.data.rawAlerts.rawAlerts.pagination.page
      this.isBusy = false
    },
    focusSearch() {
      this.$refs['searchInput'].focus()
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        search: _search != '' ? _search : undefined,
        pagination: {
          page: 1,
          show: this.limit,
        },
      }
      await this.getData(opts, true)
      this.focusSearch()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
      this.search = ''
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return
      if (this.items.length == 0 && this.search) return

      this.getData({
        pagination: {
          page: this.totalCount ? ++this.page : this.page,
          show: this.limit,
        },
        search: this.search != '' ? this.search : undefined,
      })
    },
    openDetailNotification(item) {
      const createdOn = new Date(item.createdOn).getTime()
      const id = `${item.alertCode}-${item.vehicleId}-${createdOn}-${item.alertFlag}`
      const routePath = this.$router.resolve({
        name: 'Notification Detail',
        params: {
          id: id,
        },
      })

      window.open(routePath.href, '_blank')
    },
  },
}
</script>
<style lang="scss">
@import '../style.scss';
</style>
